import { ServiceInfoDc, ServiceListItemDc } from "@evergis/api";
import { Icon, IconTypesKeys } from "@evergis/ui";
import React, { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { CardControls } from "../CardControls/CardControls";
import { CardContent } from "./CardContent";
import { ApplyLabel } from "./ApplyLabel";
import { goToMainBase } from "../../../../ducks/location/location";
import { getLocationQuery } from "../../../../ducks/location/selectors";
import { useResourceSelectorContext } from "../../../../components/ResourceSelector/ResourceSelector";
import { useLayerPreview } from "../../../../hooks/preview/usePreview";
import {
  useCurrentProjectWithAddedLayers,
  useIsLayerInProject,
} from "../../../../hooks/project/useAddLayersToCurrentProject";
import { useBaseMap } from "../../../../hooks/project/useBaseMap";
import { useProjectName } from "../../../../hooks/project/useProjectName";
import { BaseMapTheme } from "../../../MapControls/components/BasemapSelect/BasemapBlock";
import { RoutePanelName } from "../../../../appConfig/types";
import { CardProps } from "./Card";

// eslint-disable-next-line max-lines-per-function
export const LayerCard: FC<CardProps<ServiceInfoDc>> = ({ resource }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const projectName = useProjectName();
  const addLayerToCurrentProject = useCurrentProjectWithAddedLayers([resource]);
  const [preview] = useLayerPreview(resource && resource.name);
  const isAddedLayer = useIsLayerInProject(resource.name);
  const {
    onSelect: onResourceSelect,
    active: layerSelectorActive,
    selectedResourceName,
  } = useResourceSelectorContext();

  const { addAsBasemap } = useSelector(getLocationQuery);
  const { addMap, baseMapItems } = useBaseMap();

  const isAddedAsBasemap = useMemo(() => {
    if (!addAsBasemap) {
      return false;
    }

    return baseMapItems.findIndex(({ name }) => name === resource.name) !== -1;
  }, [addAsBasemap, baseMapItems, resource.name]);

  const addLayerAsBasemap = useCallback(async () => {
    await addMap({
      name: resource.name,
      title: resource.alias || resource.name,
      preview: "",
      theme: BaseMapTheme.Light,
    });
    dispatch(goToMainBase({ project: projectName, panel: RoutePanelName.Layers }));
  }, [resource.name, resource.alias, addMap, projectName]);

  const layersPrefixIcons = useMemo(
    () => [
      {
        value: "PostgresLayerService,PostgresTileLayerService",
        title: t("evergisLayer", { ns: "catalog" }),
        icon: "evergis",
      },
      {
        value: "ProxyService",
        title: t("externalLayers", { ns: "catalog" }),
        icon: "externalservice",
      },
      {
        value: "RemoteTileService",
        title: t("remoteTileService", { ns: "catalog" }),
        icon: "tms",
      },
      {
        value: "LocalTileService",
        text: t("localTileService", { ns: "catalog" }),
        icon: "local_tile_service",
      },
    ],
    [t],
  );

  const titlePrefix = useMemo(() => {
    const foundIcon = layersPrefixIcons.find(item => item.value.includes((resource as ServiceListItemDc).type));

    return <Icon kind={foundIcon?.icon as IconTypesKeys} title={foundIcon?.title} />;
  }, [layersPrefixIcons, resource]);

  const onLayerSelect = useCallback(() => {
    onResourceSelect(resource);
  }, [onResourceSelect, resource]);

  const onOpen = useMemo(() => {
    if (layerSelectorActive) {
      return onLayerSelect;
    }

    if (addAsBasemap) {
      return addLayerAsBasemap;
    }

    return addLayerToCurrentProject;
  }, [
    addAsBasemap,
    addLayerAsBasemap,
    addLayerToCurrentProject,
    layerSelectorActive,
    onLayerSelect,
    preview,
    resource,
  ]);

  return (
    <CardContent
      resource={resource}
      preview={preview}
      titlePrefix={titlePrefix}
      applyLabel={
        <ApplyLabel
          text={layerSelectorActive ? t("actions.select", { ns: "common" }) : t("actions.add", { ns: "common" })}
          appliedText={layerSelectorActive ? t("selected", { ns: "catalog" }) : t("labelAdded", { ns: "catalog" })}
          applied={
            (!addAsBasemap && !layerSelectorActive && isAddedLayer) ||
            (addAsBasemap && !layerSelectorActive && isAddedAsBasemap) ||
            (layerSelectorActive && selectedResourceName === resource.name)
          }
        />
      }
      disabled={
        !projectName ||
        (!addAsBasemap && isAddedLayer) ||
        (addAsBasemap && isAddedAsBasemap) ||
        (layerSelectorActive && selectedResourceName === resource.name)
      }
      onOpen={onOpen}
    >
      <CardControls
        resource={resource}
        config={{
          infoIcon: {
            title: t("layerInfo", { ns: "catalog" }),
          },
        }}
      />
    </CardContent>
  );
};
